import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import TrialBanner from '../components/TrialBanner';
import PullToRefresh from '../components/PullToRefresh';
import { toast } from 'sonner';
import { 
  Clock, 
  DollarSign, 
  Briefcase,
  Calendar,
  CheckSquare,
  ArrowRight,
  LogOut,
  Shield,
  User,
  TrendingUp,
  BarChart3,
  Info,
  Plus,
  Loader2,
  Play,
  Square,
  Timer,
  Mic,
  MicOff,
  Brain,
  Sparkles,
  FileText,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function DailyHoursChart({ dailyHours }) {
  if (!dailyHours || dailyHours.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <div className="text-center">
          <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No billable hours recorded yet</p>
          <p className="text-xs mt-1">Start logging time in your cases to see data here</p>
        </div>
      </div>
    );
  }

  const last14Days = dailyHours.slice(-14);
  const maxHours = Math.max(...last14Days.map(d => d.total_hours), 1);

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-1.5 h-48" data-testid="daily-hours-chart">
        {last14Days.map((day, i) => {
          const billableHeight = (day.billable_hours / maxHours) * 100;
          const nonBillableHeight = (day.non_billable_hours / maxHours) * 100;
          const dateObj = new Date(day.date + 'T12:00:00');
          const dayLabel = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
          const dateLabel = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          
          return (
            <div key={day.date} className="flex-1 flex flex-col items-center gap-1 group relative">
              <div className="w-full flex flex-col justify-end h-40">
                {day.non_billable_hours > 0 && (
                  <div 
                    className="w-full bg-slate-300 dark:bg-slate-600 rounded-t-sm transition-all"
                    style={{ height: `${nonBillableHeight}%`, minHeight: day.non_billable_hours > 0 ? '3px' : 0 }}
                  />
                )}
                <div 
                  className="w-full bg-navy dark:bg-bronze rounded-t-sm transition-all"
                  style={{ height: `${billableHeight}%`, minHeight: day.billable_hours > 0 ? '3px' : 0 }}
                />
              </div>
              <div className="text-[10px] text-muted-foreground leading-none hidden sm:block">{dayLabel}</div>
              <div className="text-[10px] text-muted-foreground leading-none">{dateLabel.split(' ')[1]}</div>
              
              {/* Tooltip */}
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-popover border rounded-md px-2.5 py-1.5 shadow-md text-xs hidden group-hover:block z-10 whitespace-nowrap">
                <p className="font-medium">{dateLabel}</p>
                <p className="text-navy dark:text-bronze">{day.billable_hours}h billable</p>
                {day.non_billable_hours > 0 && <p className="text-muted-foreground">{day.non_billable_hours}h non-billable</p>}
                {day.amount > 0 && <p className="text-emerald-600">${day.amount.toFixed(0)}</p>}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-navy dark:bg-bronze" />
          <span>Billable</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-slate-300 dark:bg-slate-600" />
          <span>Non-billable</span>
        </div>
      </div>
    </div>
  );
}

export default function ProductHubPage() {
  const navigate = useNavigate();
  const { user, token, logout, getTrialDaysRemaining } = useAuth();
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [timeEntry, setTimeEntry] = useState({ case_id: '', description: '', hours: '', entry_type: 'billable' });
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerCaseId, setTimerCaseId] = useState('');

  // Meeting capture state
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [meetingCaseId, setMeetingCaseId] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [wiretapAcknowledged, setWiretapAcknowledged] = useState(false);
  const [meetingStep, setMeetingStep] = useState('setup'); // setup | recording | processing | done
  const [processingStage, setProcessingStage] = useState('');
  const [meetingResult, setMeetingResult] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [hoursRes, casesRes] = await Promise.all([
        fetch(`${API_URL}/api/case-management/daily-hours?days=30`, { headers }),
        fetch(`${API_URL}/api/case-management/cases`, { headers })
      ]);
      if (hoursRes.ok) setDashData(await hoursRes.json());
      if (casesRes.ok) setCases(await casesRes.json());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Timer effect
  useEffect(() => {
    let interval;
    if (timerRunning) {
      interval = setInterval(() => setTimerSeconds(s => s + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning]);

  const formatTimer = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleStartTimer = () => {
    if (!timerCaseId) { toast.error('Select a case first'); return; }
    setTimerRunning(true);
  };

  const handleStopTimer = () => {
    setTimerRunning(false);
    const hours = parseFloat((timerSeconds / 3600).toFixed(2));
    setTimeEntry(prev => ({ ...prev, case_id: timerCaseId, hours: hours.toString() }));
    setTimerSeconds(0);
    setTimerCaseId('');
  };

  // Meeting capture functions
  const startMeetingRecording = async () => {
    if (!wiretapAcknowledged) {
      toast.error('Please acknowledge the recording notice first');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      setMeetingStep('recording');

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
    } catch {
      toast.error('Failed to access microphone. Please allow microphone access.');
    }
  };

  const stopMeetingRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };

  const processMeetingRecording = async () => {
    if (!audioBlob || !meetingCaseId) return;
    setMeetingStep('processing');

    try {
      // Step 1: Create communication
      setProcessingStage('Creating communication record...');
      const commRes = await fetch(`${API_URL}/api/case-management/cases/${meetingCaseId}/communications`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: meetingCaseId,
          communication_type: 'meeting',
          direction: 'outbound',
          subject: 'Meeting Recording',
          description: `Recorded meeting - Duration: ${formatRecordingTime(recordingTime)}`,
          contact_name: 'Meeting Participants',
          date: new Date().toISOString().split('T')[0],
          time: new Date().toTimeString().slice(0, 5),
          duration_minutes: Math.ceil(recordingTime / 60),
          has_recording: true
        })
      });

      if (!commRes.ok) throw new Error('Failed to create communication');
      const comm = await commRes.json();

      // Step 2: Upload audio
      setProcessingStage('Uploading audio...');
      const formData = new FormData();
      formData.append('audio', audioBlob, 'meeting_recording.webm');
      const uploadRes = await fetch(`${API_URL}/api/case-management/communications/${comm.id}/upload-audio`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!uploadRes.ok) throw new Error('Failed to upload audio');

      // Step 3: Transcribe
      setProcessingStage('Transcribing audio with AI...');
      const transcribeRes = await fetch(`${API_URL}/api/case-management/communications/${comm.id}/transcribe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      let transcript = null;
      if (transcribeRes.ok) {
        const transcriptData = await transcribeRes.json();
        transcript = transcriptData.transcription;
      }

      // Step 4: AI Summary
      let summary = null;
      if (transcript) {
        setProcessingStage('Generating AI summary...');
        const analyzeRes = await fetch(`${API_URL}/api/case-management/communications/${comm.id}/analyze`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (analyzeRes.ok) {
          const analyzeData = await analyzeRes.json();
          summary = analyzeData.ai_summary;
        }
      }

      setMeetingResult({
        transcript: transcript || 'Transcription unavailable',
        summary: summary || 'Summary unavailable',
        caseId: meetingCaseId,
        caseName: cases.find(c => c.id === meetingCaseId)?.name || 'Case',
        duration: formatRecordingTime(recordingTime)
      });
      setMeetingStep('done');
      toast.success('Meeting captured, transcribed, and summarized!');

    } catch (err) {
      toast.error(err.message || 'Processing failed');
      setMeetingStep('recording');
    }
  };

  const resetMeetingDialog = () => {
    setShowMeetingDialog(false);
    setMeetingStep('setup');
    setMeetingCaseId('');
    setAudioBlob(null);
    setRecordingTime(0);
    setWiretapAcknowledged(false);
    setProcessingStage('');
    setMeetingResult(null);
  };

  const formatRecordingTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleSubmitTimeEntry = async () => {
    if (!timeEntry.case_id || !timeEntry.description || !timeEntry.hours) {
      toast.error('Fill in case, description, and hours');
      return;
    }
    setSubmitting(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(`${API_URL}/api/case-management/cases/${timeEntry.case_id}/time-entries`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: timeEntry.case_id,
          description: timeEntry.description,
          hours: parseFloat(timeEntry.hours),
          entry_type: timeEntry.entry_type,
          date: today
        })
      });
      if (res.ok) {
        toast.success(`${timeEntry.hours}h logged successfully`);
        setTimeEntry({ case_id: '', description: '', hours: '', entry_type: 'billable' });
        fetchDashboardData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to log time');
      }
    } catch {
      toast.error('Failed to log time');
    } finally {
      setSubmitting(false);
    }
  };

  const trialDays = getTrialDaysRemaining();
  const isOnTrial = user?.subscription_status === 'trial' && trialDays !== null;
  const summary = dashData?.summary || {};

  return (
    <PullToRefresh onRefresh={fetchDashboardData} className="min-h-screen bg-background">
      <div data-testid="product-hub-page">
        <TrialBanner />
        
        {/* Header */}
        <header className="border-b bg-card/95 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <div className="flex items-center gap-2 sm:gap-3">
                <img src="/logo.png" alt="Legal AIDynamics" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-contain" />
                <div>
                  <span className="font-heading text-base sm:text-xl font-bold text-navy dark:text-white">Legal AI</span>
                  <span className="font-heading text-base sm:text-xl font-bold text-bronze">Dynamics</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 sm:gap-4">
                <Link to="/about">
                  <Button variant="ghost" size="sm" className="px-2 sm:px-3">
                    <Info className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">About</span>
                  </Button>
                </Link>
                <Link to="/security">
                  <Button variant="ghost" size="sm" className="px-2 sm:px-3">
                    <Shield className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Security</span>
                  </Button>
                </Link>
                <div className="h-6 w-px bg-border hidden sm:block" />
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Welcome */}
          <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-xl sm:text-3xl font-heading font-bold text-foreground">
                Welcome back, {user?.name?.split(' ')[0]}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Here's your billable hours overview
              </p>
              {isOnTrial && (
                <Badge variant="outline" className="mt-2 text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  Trial: {trialDays} day{trialDays === 1 ? '' : 's'} remaining
                </Badge>
              )}
            </div>
            <Button
              onClick={() => setShowMeetingDialog(true)}
              className="bg-navy hover:bg-navy-light"
              data-testid="meeting-capture-btn"
            >
              <Mic className="w-4 h-4 mr-2" />
              Capture Meeting
            </Button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <Card data-testid="stat-today-hours">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-navy/10 dark:bg-navy/30">
                    <Clock className="w-5 h-5 text-navy dark:text-bronze" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Today</p>
                    <p className="text-lg sm:text-xl font-bold font-heading">{summary.today_hours || 0}h</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-week-hours">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">This Week</p>
                    <p className="text-lg sm:text-xl font-bold font-heading">{summary.week_hours || 0}h</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-month-hours">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">This Month</p>
                    <p className="text-lg sm:text-xl font-bold font-heading">{summary.month_hours || 0}h</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-month-amount">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                    <DollarSign className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Revenue</p>
                    <p className="text-lg sm:text-xl font-bold font-heading">${(summary.month_amount || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Daily Hours Chart */}
          <Card className="mb-6" data-testid="daily-hours-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base sm:text-lg font-heading">Daily Billable Hours</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-48">
                  <div className="animate-pulse text-muted-foreground text-sm">Loading chart...</div>
                </div>
              ) : (
                <DailyHoursChart dailyHours={dashData?.daily_hours || []} />
              )}
            </CardContent>
          </Card>

          {/* Quick Time Entry + Timer */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* Quick Log */}
            <Card data-testid="quick-time-entry">
              <CardHeader className="pb-2">
                <CardTitle className="text-base sm:text-lg font-heading flex items-center gap-2">
                  <Plus className="w-4 h-4 text-navy" />
                  Quick Time Entry
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Case</Label>
                    <Select value={timeEntry.case_id} onValueChange={(v) => setTimeEntry(prev => ({ ...prev, case_id: v }))}>
                      <SelectTrigger className="h-9" data-testid="time-entry-case-select">
                        <SelectValue placeholder="Select case..." />
                      </SelectTrigger>
                      <SelectContent>
                        {cases.filter(c => c.status === 'active').map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Hours</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="0.5"
                      value={timeEntry.hours}
                      onChange={(e) => setTimeEntry(prev => ({ ...prev, hours: e.target.value }))}
                      className="h-9"
                      data-testid="time-entry-hours-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Description</Label>
                  <Input
                    placeholder="What did you work on?"
                    value={timeEntry.description}
                    onChange={(e) => setTimeEntry(prev => ({ ...prev, description: e.target.value }))}
                    className="h-9"
                    data-testid="time-entry-description-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitTimeEntry(); }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Select value={timeEntry.entry_type} onValueChange={(v) => setTimeEntry(prev => ({ ...prev, entry_type: v }))}>
                    <SelectTrigger className="h-8 w-32 text-xs" data-testid="time-entry-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="billable">Billable</SelectItem>
                      <SelectItem value="non_billable">Non-billable</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={handleSubmitTimeEntry}
                    disabled={submitting || !timeEntry.case_id || !timeEntry.description || !timeEntry.hours}
                    className="bg-navy hover:bg-navy-light"
                    data-testid="time-entry-submit-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                    Log Time
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Timer */}
            <Card data-testid="timer-widget">
              <CardHeader className="pb-2">
                <CardTitle className="text-base sm:text-lg font-heading flex items-center gap-2">
                  <Timer className="w-4 h-4 text-navy" />
                  Timer
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-center py-3">
                  <span className={`font-mono text-3xl sm:text-4xl font-bold tracking-wider ${timerRunning ? 'text-navy dark:text-bronze' : 'text-muted-foreground'}`} data-testid="timer-display">
                    {formatTimer(timerSeconds)}
                  </span>
                </div>
                {!timerRunning ? (
                  <>
                    <Select value={timerCaseId} onValueChange={setTimerCaseId}>
                      <SelectTrigger className="h-9" data-testid="timer-case-select">
                        <SelectValue placeholder="Select case to time..." />
                      </SelectTrigger>
                      <SelectContent>
                        {cases.filter(c => c.status === 'active').map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      className="w-full bg-navy hover:bg-navy-light"
                      onClick={handleStartTimer}
                      disabled={!timerCaseId}
                      data-testid="timer-start-btn"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Start Timer
                    </Button>
                  </>
                ) : (
                  <Button
                    className="w-full"
                    variant="destructive"
                    onClick={handleStopTimer}
                    data-testid="timer-stop-btn"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    Stop & Log Time
                  </Button>
                )}
                {timerRunning && (
                  <p className="text-xs text-center text-muted-foreground">
                    Timing: {cases.find(c => c.id === timerCaseId)?.name || 'Unknown case'}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <Card 
              className="cursor-pointer hover:border-navy/30 transition-colors group"
              onClick={() => navigate('/case-management')}
              data-testid="nav-cases"
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-navy/10 dark:bg-navy/30">
                    <Briefcase className="w-5 h-5 text-navy dark:text-bronze" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Cases</p>
                    <p className="text-xs text-muted-foreground">{summary.active_cases || 0} active cases</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:border-navy/30 transition-colors group"
              onClick={() => navigate('/case-management?tab=cases')}
              data-testid="nav-events"
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Upcoming Events</p>
                    <p className="text-xs text-muted-foreground">{summary.upcoming_events || 0} scheduled</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:border-navy/30 transition-colors group"
              onClick={() => navigate('/case-management?tab=cases')}
              data-testid="nav-tasks"
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                    <CheckSquare className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Pending Tasks</p>
                    <p className="text-xs text-muted-foreground">{summary.pending_tasks || 0} tasks</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
              </CardContent>
            </Card>
          </div>

          {/* Meeting Capture Dialog */}
          <Dialog open={showMeetingDialog} onOpenChange={(open) => { if (!open && !isRecording && meetingStep !== 'processing') resetMeetingDialog(); }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mic className="w-5 h-5 text-navy" />
                  Meeting Capture
                </DialogTitle>
                <DialogDescription>
                  Record, transcribe, and summarize meetings with AI
                </DialogDescription>
              </DialogHeader>

              {meetingStep === 'setup' && (
                <div className="space-y-4 py-2">
                  <div>
                    <Label>Link to Case</Label>
                    <Select value={meetingCaseId} onValueChange={setMeetingCaseId}>
                      <SelectTrigger data-testid="meeting-case-select">
                        <SelectValue placeholder="Select a case..." />
                      </SelectTrigger>
                      <SelectContent>
                        {cases.filter(c => c.status === 'active').map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <Checkbox
                      id="wiretap"
                      checked={wiretapAcknowledged}
                      onCheckedChange={setWiretapAcknowledged}
                      data-testid="wiretap-checkbox"
                    />
                    <label htmlFor="wiretap" className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed cursor-pointer">
                      I confirm that all participants have been notified this meeting is being recorded, in compliance with applicable wiretapping and consent laws.
                    </label>
                  </div>

                  <Button
                    className="w-full bg-navy hover:bg-navy-light"
                    disabled={!meetingCaseId || !wiretapAcknowledged}
                    onClick={startMeetingRecording}
                    data-testid="meeting-start-btn"
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Start Recording
                  </Button>
                </div>
              )}

              {meetingStep === 'recording' && (
                <div className="space-y-4 py-4">
                  <div className="flex flex-col items-center">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center animate-pulse">
                        <Mic className="w-10 h-10 text-red-600" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 animate-pulse" />
                    </div>
                    <span className="font-mono text-2xl font-bold mt-4 text-red-600" data-testid="meeting-timer">
                      {formatRecordingTime(recordingTime)}
                    </span>
                    <p className="text-sm text-muted-foreground mt-1">
                      Recording: {cases.find(c => c.id === meetingCaseId)?.name}
                    </p>
                  </div>

                  <Button
                    className="w-full"
                    variant="destructive"
                    onClick={stopMeetingRecording}
                    data-testid="meeting-stop-btn"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    Stop Recording
                  </Button>

                  {!isRecording && audioBlob && (
                    <Button
                      className="w-full bg-navy hover:bg-navy-light"
                      onClick={processMeetingRecording}
                      data-testid="meeting-process-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Transcribe & Summarize
                    </Button>
                  )}
                </div>
              )}

              {meetingStep === 'processing' && (
                <div className="flex flex-col items-center py-8 space-y-4">
                  <Loader2 className="w-10 h-10 animate-spin text-navy" />
                  <p className="text-sm font-medium">{processingStage}</p>
                  <div className="w-full space-y-2">
                    {['Creating communication record...', 'Uploading audio...', 'Transcribing audio with AI...', 'Generating AI summary...'].map((stage, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        {processingStage === stage ? (
                          <Loader2 className="w-3 h-3 animate-spin text-navy" />
                        ) : ['Creating communication record...', 'Uploading audio...', 'Transcribing audio with AI...', 'Generating AI summary...'].indexOf(processingStage) > i ? (
                          <CheckCircle className="w-3 h-3 text-green-600" />
                        ) : (
                          <div className="w-3 h-3 rounded-full border border-muted-foreground/30" />
                        )}
                        <span className={processingStage === stage ? 'text-foreground font-medium' : 'text-muted-foreground'}>{stage}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {meetingStep === 'done' && meetingResult && (
                <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
                  <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">Meeting captured successfully</p>
                      <p className="text-xs text-green-600">{meetingResult.caseName} | Duration: {meetingResult.duration}</p>
                    </div>
                  </div>

                  {meetingResult.summary && meetingResult.summary !== 'Summary unavailable' && (
                    <div>
                      <Label className="text-xs font-medium flex items-center gap-1 mb-1">
                        <Brain className="w-3 h-3" /> AI Summary
                      </Label>
                      <div className="p-3 bg-muted/50 rounded-lg text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {meetingResult.summary}
                      </div>
                    </div>
                  )}

                  {meetingResult.transcript && meetingResult.transcript !== 'Transcription unavailable' && (
                    <div>
                      <Label className="text-xs font-medium flex items-center gap-1 mb-1">
                        <FileText className="w-3 h-3" /> Transcript
                      </Label>
                      <div className="p-3 bg-muted/50 rounded-lg text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {meetingResult.transcript}
                      </div>
                    </div>
                  )}

                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/case-management/${meetingResult.caseId}`)}
                    >
                      View in Case
                    </Button>
                    <Button onClick={resetMeetingDialog}>Done</Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </PullToRefresh>
  );
}
